import React, { useEffect, Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import { UncontrolledTooltip } from "reactstrap";

import Breadcrumb from "../../../../components/common/breadcrumb";
import { showToast } from "../../../../components/ToastifyNotification";

// ✅ DELIVERY APIs
import {
  GetDeliveries,
  DeleteDelivery
} from "../../../../api/ipd/deliveryAPI";
import { DeletePatientHistory, GetPatientHistories } from "../../../../api/ipd/patientHistoryAPI";

const ListPatientHistoryEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ================= FETCH DATA =================
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetPatientHistories();
      if (response.success) {
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,

          ipdNo: item?.ipdEntryId?.ipdNo || "",
          patientName: item?.ipdEntryId?.patientId?.patientName || "",
          sex: item?.ipdEntryId?.patientId?.sex || "",
          age: item?.ipdEntryId?.patientId?.age || "",

          complaint: item.complaint || "",
          historyOfPresentIllness: item.historyOfPresentIllness || "",
          historyOfPastIllness: item.historyOfPastIllness || "",
          familyHistory: item.familyHistory || "",
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ================= SEARCH =================
  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  // ================= ACTIONS =================
  const handleEditClick = id => {
    navigate(`/ipd/transaction/patient-history-entry/edit/${id}`);
  };

  const handleDeleteClick = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This delivery entry will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeletePatientHistory(id);
          if (response.success) {
            showToast("success", response.message);
            const newData = data.filter(item => item.id !== id);
            setData(newData);
            setFilteredData(newData);
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  // ================= TABLE COLUMNS =================
  const columns = [
    { name: "S.No", selector: row => row.index, width: "6%" },
    { name: "IPD No", selector: row => row.ipdNo, sortable: true, width: "10%" },
    { name: "Patient Name", selector: row => row.patientName, sortable: true, width: "15%" },
    { name: "Sex", selector: row => row.sex, width: "6%" },
    { name: "Age", selector: row => row.age, width: "6%" },

    { name: "Complaint", selector: row => row.complaint, sortable: true, width: "20%" },
    { name: "History of Present Illness", selector: row => row.historyOfPresentIllness, sortable: true, width: "20%" },
    { name: "History of Past Illness", selector: row => row.historyOfPastIllness, sortable: true, width: "20%" },
    { name: "Family History", selector: row => row.familyHistory, sortable: true, width: "20%" },

    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "20%"
    }
  ];

  // ================= CSV EXPORT =================
  const headers = [
    { label: "S.No", key: "index" },
    { label: "IPD No", key: "ipdNo" },
    { label: "Patient Name", key: "patientName" },
    { label: "Sex", key: "sex" },
    { label: "Age", key: "age" },

    { label: "Complaint", key: "complaint" },
    { label: "History of Present Illness", key: "historyOfPresentIllness" },
    { label: "History of Past Illness", key: "historyOfPastIllness" },
    { label: "Family History", key: "familyHistory" },
  ];

  const ExportCSV = ({ data }) =>
      <CSVLink data={data} headers={headers} filename="patient-history-entries.csv">
        <button className="btn btn-primary mt-1" id="export">
          Export
        </button>
        <UncontrolledTooltip placement="top" target="export">
          Export to CSV
        </UncontrolledTooltip>
      </CSVLink>;

  // ================= RENDER =================
  return (
    <Fragment>
      <Breadcrumb parent="IPD" title="Patient History Entries" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Patient History Entries</h5>

            <Link to="/ipd/transaction/patient-history-entry/add">
              <button className="btn btn-primary" id="create">
                Create Patient History Entry
              </button>
              <UncontrolledTooltip placement="top" target="create">
                Create New Patient History Entry
              </UncontrolledTooltip>
            </Link>
          </div>

          <div className="card-body">
            <div className="row mb-2">
              <div className="col-md-6">
                <input
                  className="form-control"
                  placeholder="Search..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              <div className="col-md-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListPatientHistoryEntry;
